import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import {
  getBinaryAggregateColor,
  getMetricColorForMetric,
  getMetricType,
  METRIC_TYPE,
} from "@/metrics/metric-display"

import type { MetricRecordData } from "@/types/metrics/contracts"
import { formatNumber } from "@/utils/formatting/format-number"

/**
 * Returns metric IDs sorted by frequency (most common first) across an array of rows.
 */
export function topMetricIdsByFrequency(
  rows: Array<{ metric_ids?: string[] }>,
): string[] {
  const counts = new Map<string, number>()
  for (const row of rows) {
    for (const id of row.metric_ids ?? []) {
      counts.set(id, (counts.get(id) ?? 0) + 1)
    }
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([id]) => id)
}

/**
 * Builds metric column defs from frequency-ordered IDs, sorted alphabetically by display name.
 */
export function buildMetricColumns(
  orderedIds: string[],
  metricDisplayNames: Record<string, string>,
): { key: string; label: string }[] {
  return [...orderedIds]
    .filter((id) => metricDisplayNames[id] !== undefined)
    .sort((a, b) => metricDisplayNames[a].localeCompare(metricDisplayNames[b]))
    .map((id) => ({
      key: `metric:${id}`,
      label: metricDisplayNames[id],
    }))
}

function splitSeconds(
  totalSeconds: number,
  unitsPerSecond: number,
): { sign: string; minutes: number; seconds: string; remainder: number } {
  const sign = totalSeconds < 0 ? "-" : ""
  // toPrecision drops float noise (1.005 * 100 = 100.49999…) before rounding.
  const totalUnits = Math.round(
    Number((Math.abs(totalSeconds) * unitsPerSecond).toPrecision(12)),
  )
  const unitsPerMinute = unitsPerSecond * 60
  const minutes = Math.floor(totalUnits / unitsPerMinute)
  const wholeSeconds = Math.floor(
    (totalUnits % unitsPerMinute) / unitsPerSecond,
  )
  return {
    sign,
    minutes,
    seconds: String(wholeSeconds).padStart(2, "0"),
    remainder: totalUnits % unitsPerSecond,
  }
}

/**
 * Formats seconds as `m:ss`, rounded to the nearest second (254.04 → "4:14").
 */
function formatSecondsAsMinutes(totalSeconds: number): string {
  const { sign, minutes, seconds } = splitSeconds(totalSeconds, 1)
  return `${sign}${minutes}:${seconds}`
}

/**
 * Formats seconds as `m:ss`, adding hundredths when present (254.04 → "4:14.04").
 */
function formatSecondsAsMinutesDetailed(totalSeconds: number): string {
  const { sign, minutes, seconds, remainder } = splitSeconds(totalSeconds, 100)
  const fraction =
    remainder === 0 ? "" : `.${String(remainder).padStart(2, "0")}`
  return `${sign}${minutes}:${seconds}${fraction}`
}

/**
 * Formats a metric float value for display, using percentage units for Composite
 * scalar values, `m:ss` for Audio Duration, and the standard decimal format for
 * other metrics.
 */
export function formatMetricValue(
  value: number,
  metric?: Pick<MetricRecordData, "metric_manager">,
): string {
  if (metric?.metric_manager === "manager_composite_evaluation") {
    return `${formatNumber(value * 100)}%`
  }
  if (metric?.metric_manager === "audio_duration_metric_manager") {
    return formatSecondsAsMinutes(value)
  }
  return value % 1 === 0 ? String(value) : value.toFixed(2)
}

/**
 * Returns a more precise rendering of a metric value for hover text, or `null`
 * when it would repeat what `formatMetricValue` already shows. Audio Duration
 * cells round to whole seconds, so this restores the hundredths.
 */
export function formatMetricValueDetail(
  value: number,
  metric?: Pick<MetricRecordData, "metric_manager">,
): string | null {
  if (metric?.metric_manager !== "audio_duration_metric_manager") {
    return null
  }
  const detailed = formatSecondsAsMinutesDetailed(value)
  return detailed === formatSecondsAsMinutes(value) ? null : detailed
}

export function getMetricColumnColor(
  value: number,
  metric: MetricRecordData | undefined,
  aggregate = false,
): ReturnType<typeof getMetricColorForMetric> {
  if (!metric) {
    return "default"
  }
  if (aggregate && getMetricType(metric) === METRIC_TYPE.BINARY) {
    // Run summaries provide mean event counts, not per-call binary rates.
    if (
      metric.metric_manager === "manager_agent_needs_reprompting" ||
      metric.metric_manager === "manager_custom_agent_needs_reprompting"
    ) {
      return "default"
    }
    const yesPasses =
      getMetricColorForMetric(ANNOTATION_VALUE.YES, metric) === "success"
    const noPasses =
      getMetricColorForMetric(ANNOTATION_VALUE.NO, metric) === "success"
    const passRate = (yesPasses ? value : 0) + (noPasses ? 1 - value : 0)
    return getBinaryAggregateColor(passRate, metric)
  }
  return getMetricColorForMetric(value, metric)
}
